import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { getActiveSchool } from "../context/AuthContext";
import { PageHeader } from "../components/bits";
import { FileUpload } from "../components/FileUpload";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

export default function Settings() {
  const [school, setSchool] = useState(null);
  const [form, setForm] = useState(null);
  const schoolId = getActiveSchool();

  useEffect(() => {
    if (!schoolId) return;
    api.get(`/schools/${schoolId}`).then((r) => {
      setSchool(r.data);
      setForm({
        name: r.data.name, commercial_name: r.data.commercial_name || "", rnc: r.data.rnc || "",
        address: r.data.address || "", phones: (r.data.phones || []).join(", "),
        emails: (r.data.emails || []).join(", "), ncf_prefix: r.data.ncf_prefix || "B02",
        logo_url: r.data.logo_url || "", show_name: r.data.show_name !== false,
      });
    }).catch((e) => toast.error(formatApiError(e)));
  }, [schoolId]);

  if (!schoolId) return <p className="text-slate-500">Selecciona un colegio desde la página de Colegios.</p>;
  if (!form) return <p className="text-slate-400 text-sm">Cargando configuración…</p>;

  const regUrl = `${window.location.origin}/registro/${schoolId}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(regUrl)}`;

  const isUploaded = (v) => v?.startsWith("data:") || v?.startsWith("/api/files/");

  const save = async () => {
    try {
      await api.put(`/schools/${schoolId}`, {
        ...form,
        phones: form.phones.split(",").map((s) => s.trim()).filter(Boolean),
        emails: form.emails.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="Configuración del colegio" subtitle="Datos institucionales, fiscales y QR de registro" />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Nombre</Label><Input data-testid="cfg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Nombre comercial</Label><Input data-testid="cfg-commercial" value={form.commercial_name} onChange={(e) => setForm({ ...form, commercial_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>RNC</Label><Input data-testid="cfg-rnc" value={form.rnc} onChange={(e) => setForm({ ...form, rnc: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Prefijo NCF</Label><Input data-testid="cfg-ncf" value={form.ncf_prefix} onChange={(e) => setForm({ ...form, ncf_prefix: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Dirección</Label><Input data-testid="cfg-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="space-y-1.5">
            <Label>Logo del colegio (aparece en las facturas PDF y correos)</Label>
            <div className="flex gap-2 items-center">
              <Input data-testid="cfg-logo" placeholder="https://… o sube un archivo" value={isUploaded(form.logo_url) ? "(archivo subido)" : form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} />
              <FileUpload category="branding" accept="image/*" testid="cfg-logo-file" label="Subir"
                onUploaded={(f) => setForm((prev) => ({ ...prev, logo_url: f.url }))} />
            </div>
            {form.logo_url && (
              <img src={form.logo_url} alt="logo del colegio" className="h-12 w-auto object-contain rounded border border-slate-200 p-1 mt-1" data-testid="cfg-logo-preview" />
            )}
          </div>
          <div className="flex items-center gap-3">
            <Switch
              data-testid="cfg-show-name"
              checked={form.show_name !== false}
              onCheckedChange={(v) => setForm({ ...form, show_name: v })}
            />
            <Label className="!mt-0">Mostrar el nombre del colegio junto al logo en las facturas</Label>
          </div>
          <div className="space-y-1.5"><Label>Teléfonos (coma)</Label><Input data-testid="cfg-phones" value={form.phones} onChange={(e) => setForm({ ...form, phones: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Correos (coma)</Label><Input data-testid="cfg-emails" value={form.emails} onChange={(e) => setForm({ ...form, emails: e.target.value })} /></div>
          <div className="pt-2">
            <Button data-testid="save-settings-button" className="bg-blue-800 hover:bg-blue-700" onClick={save}>Guardar configuración</Button>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm text-center space-y-3">
          <div className="flex items-center justify-center gap-2 text-slate-800 font-semibold">
            <QrCode className="w-5 h-5" /> Registro de padres
          </div>
          <p className="text-xs text-slate-500">Los padres escanean este QR para registrarse en tu colegio.</p>
          <img src={qrUrl} alt="QR de registro" className="mx-auto rounded-lg border border-slate-200" data-testid="registration-qr-image" />
          <p className="text-xs font-mono text-slate-400 break-all" data-testid="registration-url">{regUrl}</p>
          <Button variant="outline" size="sm" data-testid="copy-reg-url" onClick={() => { navigator.clipboard.writeText(regUrl); toast.success("Enlace copiado"); }}>
            Copiar enlace
          </Button>
        </div>
      </div>
    </div>
  );
}
